import {CM_LOGGER_GRAPH} from "@app/common/services/logging"
import {Component, computed, ElementRef, HostListener, inject, input, ChangeDetectionStrategy} from "@angular/core"
import {NodeEditorTransformService} from "@app/node-editor/services/node-editor-transform.service"
import {SocketPosition} from "@socket-editor/types"
import {ConnectedSocket, SocketConnectionsService} from "@app/socket-editor/services/socket-connections.service"
import {v4 as uuid} from "uuid"
import {colorForValueType} from "@material-editor/helpers/color"
import {NodesService} from "@node-editor/services/nodes.service"
import {MaterialNode, SettingValueType} from "@cm/material-nodes"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-node-socket",
    templateUrl: "./node-socket.component.html",
    styleUrls: ["./node-socket.component.scss"],
    host: {
        "[class.cm-input-socket]": "$type()==='input'",
        "[class.cm-output-socket]": "$type()==='output'",
        "[style.background-color]": "$color()",
    },
})
export class NodeSocketComponent {
    private readonly logger = inject(CM_LOGGER_GRAPH)
    readonly $type = input.required<"input" | "output">({alias: "type"})
    readonly $nodeId = input.required<string>({alias: "nodeId"})
    readonly $socketId = input.required<string>({alias: "socketId"})
    readonly $valueType = input.required<SettingValueType>({alias: "valueType"})

    readonly $color = computed(() => colorForValueType(this.$valueType()))

    readonly transformService = inject(NodeEditorTransformService)
    readonly nodes = inject(NodesService<MaterialNode>)
    readonly connections = inject(SocketConnectionsService)

    static pulledConnectionId = uuid()

    @HostListener("mousedown", ["$event"]) onMouseDown(event: MouseEvent) {
        event.stopPropagation()
        const adjustedPosition = this.transformService.viewportToLogicalCoordinates({x: event.clientX, y: event.clientY})
        // special case: if a node is dragged from an input socket,
        // we remove that connection and start pulling a new one from the previous source
        if (this.$type() === "input") {
            const existingConnection = this.connections.inputs(this.$nodeId(), this.$socketId())?.[0]
            if (existingConnection) {
                this.connections.remove(existingConnection.id)
                this.connections.add({
                    id: NodeSocketComponent.pulledConnectionId,
                    source: existingConnection.source,
                    destination: {position: {x: adjustedPosition.x, y: adjustedPosition.y}},
                })
                return
            }

            // start pulling a new connection from the mouse position to the input socket
            this.connections.add({
                id: NodeSocketComponent.pulledConnectionId,
                source: {position: {x: adjustedPosition.x, y: adjustedPosition.y}},
                destination: this.getSocket(),
            })
        } else {
            // start pulling a new connection
            this.connections.add({
                id: NodeSocketComponent.pulledConnectionId,
                source: this.getSocket(),
                destination: {position: {x: adjustedPosition.x, y: adjustedPosition.y}},
            })
        }
    }

    @HostListener("document:mousemove", ["$event"]) onDocumentMouseMove(event: MouseEvent) {
        const pulledConnection = this.connections.get(NodeSocketComponent.pulledConnectionId)
        if (pulledConnection) {
            const adjustedPosition = this.transformService.viewportToLogicalCoordinates({x: event.clientX, y: event.clientY})
            if (pulledConnection.destination.nodeId) {
                this.connections.update({
                    id: NodeSocketComponent.pulledConnectionId,
                    source: {position: adjustedPosition, refreshPosition: async () => this.getPosition()},
                    destination: pulledConnection.destination,
                })
            } else {
                this.connections.update({
                    id: NodeSocketComponent.pulledConnectionId,
                    source: pulledConnection.source,
                    destination: {position: adjustedPosition, refreshPosition: async () => this.getPosition()},
                })
            }
        }
    }

    @HostListener("document:mouseup", ["$event"]) onDocumentMouseUp(_event: MouseEvent) {
        const pulledConnection = this.connections.get(NodeSocketComponent.pulledConnectionId)
        if (pulledConnection) {
            this.connections.remove(NodeSocketComponent.pulledConnectionId)
        }
    }

    @HostListener("mouseup", ["$event"]) onMouseUp(_event: MouseEvent) {
        const pulledConnection = this.connections.get(NodeSocketComponent.pulledConnectionId)
        if (pulledConnection) {
            if (pulledConnection.source.nodeId && pulledConnection.source.socketId) {
                // we are pulling from an output to the current node
                if (this.$type() === "output") {
                    this.logger.warn("Trying to connect output to output. This is invalid.")
                    return
                }
                const source = pulledConnection.source
                const destination = this.getSocket()

                // input sockets can't have more than one input, so we need to clear the previous connection, if any
                this.connections.clearSocket(destination.nodeId, destination.socketId, "input")
                // a node should not connect to itself
                if (source.nodeId !== destination.nodeId) {
                    this.connections.add([
                        {
                            id: uuid(),
                            source,
                            destination,
                        },
                    ])
                }
            } else {
                // we are pulling from an input to the current node
                if (this.$type() === "input") {
                    this.logger.warn("Trying to connect input to input. This is invalid.")
                    return
                }
                const source = this.getSocket()
                const destination = pulledConnection.destination as ConnectedSocket
                if (!(pulledConnection.destination.nodeId && pulledConnection.destination.socketId)) {
                    throw new Error("Invalid pulled connection: Neither source nor destination is a valid node socket.")
                }
                // a node should not connect to itself
                if (source.nodeId !== destination.nodeId) {
                    this.connections.add([
                        {
                            id: uuid(),
                            source,
                            destination,
                        },
                    ])
                }
            }
        }
    }

    private elementRef = inject(ElementRef)

    getPosition(): SocketPosition {
        const boundingRect: DOMRect = this.elementRef.nativeElement.getBoundingClientRect()
        return this.transformService.viewportToLogicalCoordinates({
            x: boundingRect.x + boundingRect.width / 2,
            y: boundingRect.y + boundingRect.height / 2,
        })
    }

    getSocket(): ConnectedSocket {
        return {
            nodeId: this.$nodeId(),
            socketId: this.$socketId(),
            position: this.getPosition(),
            color: colorForValueType(this.$valueType()),
            refreshPosition: async () => this.getPosition(),
        }
    }
}
